import React, { useEffect, useState } from 'react';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import { MonthlyReportCard } from './MonthlyReportCard';
import Avatar from '../ui/Avatar';

interface LeaderEntry {
  id: string;
  name: string;
  avatar?: string | null;
}

interface StatsData {
  groupInfo: { totalMatches: number };
  topScorers: (LeaderEntry & { totalGoals: number })[];
  topStreaks: (LeaderEntry & { currentStreak: number })[];
  topAttendance: (LeaderEntry & { attendance: number; playedMatches: number })[];
  topEffort?: (LeaderEntry & { avgCalories: number; trackedMatches: number })[];
  mvp: (LeaderEntry & { rating: number }) | null;
  availableYears?: string[];
}

type Period = 'month' | 'all';

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const MEDALS = ['', '🥇', '🥈', '🥉'];

interface PodiumCardProps {
  entry: LeaderEntry;
  rank: number;
  stat: string | number;
  label: string;
}

const PodiumCard: React.FC<PodiumCardProps> = ({ entry, rank, stat, label }) => (
  <div
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      background: theme.colors.surface,
      borderRadius: 12,
      border: `1px solid ${theme.colors.border}`,
      padding: '12px 6px',
      gap: 4,
      minWidth: 0,
    }}
  >
    <span style={{ fontSize: 18 }}>{MEDALS[rank] || `${rank}`}</span>
    <Avatar src={entry.avatar} name={entry.name} size={40} bg={theme.colors.border} />
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: theme.colors.text,
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'center',
      }}
    >
      {entry.name}
    </span>
    <span style={{ fontSize: 20, fontWeight: 800, color: '#F59E0B' }}>{stat}</span>
    <span style={{ fontSize: 10, color: theme.colors.textSecondary }}>{label}</span>
  </div>
);

interface RankingSectionProps {
  title: string;
  icon: string;
  entries: (LeaderEntry & Record<string, any>)[];
  renderStat: (entry: any) => { value: string | number; label: string };
}

const RankingSection: React.FC<RankingSectionProps> = ({ title, icon, entries, renderStat }) => {
  if (entries.length === 0) return null;
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div style={{ marginBottom: 24, padding: '0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{icon}</span>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
          {title}
        </h3>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
        {top3.map((entry, i) => {
          const { value, label } = renderStat(entry);
          return (
            <PodiumCard
              key={entry.id}
              entry={entry}
              rank={i + 1}
              stat={value}
              label={label}
            />
          );
        })}
      </div>

      {rest.map((entry, i) => {
        const { value, label } = renderStat(entry);
        return (
          <div
            key={entry.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '8px',
              gap: 10,
            }}
          >
            <span
              style={{
                width: 20,
                textAlign: 'center',
                fontSize: 14,
                fontWeight: 600,
                color: theme.colors.textSecondary,
              }}
            >
              {i + 4}
            </span>
            <span
              style={{
                flex: 1,
                fontSize: 14,
                color: theme.colors.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {entry.name}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#F59E0B' }}>
              {value} {label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

interface Props {
  groupId: string;
}

export const GroupLeaderboard: React.FC<Props> = ({ groupId }) => {
  const [period, setPeriod] = useState<Period>('month');
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    let query = '';
    if (period === 'month') {
      query = '?period=month';
    } else if (selectedYear) {
      query = `?year=${selectedYear}`;
    }
    fetch(getApiUrl(`/api/groups/${groupId}/stats${query}`), {
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json) setData(json);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, period, selectedYear]);

  const monthName = new Date().toLocaleDateString('es-AR', { month: 'long' });

  return (
    <div>
      {/* Period toggle */}
      <div
        style={{
          display: 'flex',
          margin: '12px 16px',
          background: theme.colors.surface,
          borderRadius: 10,
          border: `1px solid ${theme.colors.border}`,
          padding: 3,
        }}
      >
        <button
          type="button"
          onClick={() => setPeriod('month')}
          style={{
            flex: 1,
            padding: '8px',
            borderRadius: 8,
            border: 'none',
            background: period === 'month' ? theme.colors.primary : 'transparent',
            color: period === 'month' ? theme.colors.background : theme.colors.textSecondary,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
        </button>
        <button
          type="button"
          onClick={() => { setPeriod('all'); setSelectedYear(null); }}
          style={{
            flex: 1,
            padding: '8px',
            borderRadius: 8,
            border: 'none',
            background: period === 'all' ? theme.colors.primary : 'transparent',
            color: period === 'all' ? theme.colors.background : theme.colors.textSecondary,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Histórico
        </button>
      </div>

      {/* Year chips — only shown when historical period and years are available */}
      {period === 'all' && data?.availableYears && data.availableYears.length > 0 && (
        <div style={{ display: 'flex', gap: 6, margin: '0 16px 12px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setSelectedYear(null)}
            style={{
              padding: '4px 10px',
              borderRadius: 16,
              border: `1px solid ${selectedYear === null ? theme.colors.primary : theme.colors.border}`,
              background: selectedYear === null ? 'rgba(0,230,118,0.12)' : 'transparent',
              color: selectedYear === null ? theme.colors.primary : theme.colors.textSecondary,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Todos
          </button>
          {data.availableYears.map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => setSelectedYear(year)}
              style={{
                padding: '4px 10px',
                borderRadius: 16,
                border: `1px solid ${selectedYear === year ? theme.colors.primary : theme.colors.border}`,
                background: selectedYear === year ? 'rgba(0,230,118,0.12)' : 'transparent',
                color: selectedYear === year ? theme.colors.primary : theme.colors.textSecondary,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: 40,
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              border: '3px solid transparent',
              borderTop: `3px solid ${theme.colors.primary}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
          <style>{`@keyframes spin { 0%{transform:rotate(0)} 100%{transform:rotate(360deg)} }`}</style>
        </div>
      ) : !data || data.groupInfo.totalMatches === 0 ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 28 }}>🏆</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: theme.colors.text }}>
              El ranking arranca pronto
            </span>
          </div>
          <p
            style={{
              fontSize: 14,
              color: theme.colors.textSecondary,
              maxWidth: 360,
              margin: '0 auto 20px',
              lineHeight: 1.5,
            }}
          >
            Cuando se juegue el primer partido, acá van a aparecer el goleador, el que nunca falta,
            y el MVP del grupo.
          </p>
          <div
            style={{
              maxWidth: 360,
              margin: '0 auto',
              background: theme.colors.surface,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 14,
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {[
              { icon: '⚽', label: 'Goleador del grupo' },
              { icon: '🔥', label: 'Racha más larga' },
              { icon: '💪', label: 'El más fiel' },
              { icon: '👑', label: 'MVP' },
            ].map((it) => (
              <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 18, width: 28, textAlign: 'center' }}>{it.icon}</span>
                <span style={{ flex: 1, fontSize: 14, color: theme.colors.textSecondary }}>
                  {it.label}
                </span>
                <span style={{ fontSize: 14, color: theme.colors.border, fontWeight: 700 }}>
                  ---
                </span>
              </div>
            ))}
          </div>
          <p
            style={{
              fontSize: 12,
              color: theme.colors.textSecondary,
              marginTop: 16,
            }}
          >
            Al terminar el partido, cargá el resultado para que cuente.
          </p>
        </div>
      ) : (
        <>
          {/* Monthly report */}
          <MonthlyReportCard groupId={groupId} />

          {/* Live rankings */}
          <div style={{ padding: '8px 16px 4px' }}>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: theme.colors.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {period === 'month' ? 'Ranking del mes' : 'Ranking historico'}
            </span>
          </div>

          <RankingSection
            title="Goleadores"
            icon="⚽"
            entries={data.topScorers}
            renderStat={(e) => ({ value: e.totalGoals, label: 'goles' })}
          />

          <RankingSection
            title="Racha de fuego"
            icon="🔥"
            entries={data.topStreaks}
            renderStat={(e) => ({ value: e.currentStreak, label: 'seguidos' })}
          />

          <RankingSection
            title="Los Incondicionales"
            icon="💪"
            entries={data.topAttendance}
            renderStat={(e) => ({ value: `${e.attendance}%`, label: 'asistencia' })}
          />

          <RankingSection
            title="Más esfuerzo"
            icon="⚡"
            entries={data.topEffort || []}
            renderStat={(e) => ({ value: e.avgCalories, label: 'kcal/partido' })}
          />

          {data.mvp && (
            <div
              style={{
                margin: '0 16px 20px',
                background: theme.colors.surface,
                border: '1px solid #F59E0B',
                borderRadius: 14,
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 28 }}>👑</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#F59E0B' }}>MVP del grupo</span>
              <Avatar src={data.mvp.avatar} name={data.mvp.name} size={48} bg={theme.colors.border} />
              <span style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text }}>
                {data.mvp.name}
              </span>
            </div>
          )}

          <div
            style={{
              fontSize: 12,
              color: theme.colors.textSecondary,
              textAlign: 'center',
              marginTop: 8,
              paddingBottom: 16,
            }}
          >
            {data.groupInfo.totalMatches} partido{data.groupInfo.totalMatches !== 1 ? 's' : ''} jugado
            {data.groupInfo.totalMatches !== 1 ? 's' : ''}
          </div>
        </>
      )}
    </div>
  );
};

export default GroupLeaderboard;
